import type { ReactNode } from "react";
import { RETRY_COPY, type RetryReason } from "./tenant-gate.ts";
import { Button, ErrorText, Feedback } from "./ui/index.ts";

export const UnavailableCard = ({ onSignOut }: { onSignOut: () => void }): ReactNode => (
  <section className="access-denied" aria-labelledby="access-denied-title">
    <div className="access-denied__status" aria-hidden="true">
      Access restricted
    </div>
    <h1 id="access-denied-title" className="t-display">
      You don’t have access to this portal
    </h1>
    <p className="t-body measure">Your account is signed in, but it hasn’t been granted portal access. Contact your administrator if you believe you should have access.</p>
    <div className="gate-actions">
      <Button onClick={onSignOut}>Sign out</Button>
    </div>
  </section>
);

// Deliberately identical for an unknown tenant and a tenant the visitor does not belong to, so slugs cannot be probed.
export const NoAccessHereCard = ({ onGo, onSignOut }: { onGo: () => void; onSignOut: () => void }): ReactNode => (
  <section className="stack" aria-labelledby="no-access-here-title">
    <h1 id="no-access-here-title" className="t-display">
      Not found or no access here
    </h1>
    <p className="t-body measure">This page does not exist, or your account cannot use it. Go to your own portal, or sign out to sign in with a different account.</p>
    <div className="row gate-actions">
      <Button variant="primary" onClick={onGo}>
        Go to your portal
      </Button>
      <Button onClick={onSignOut}>Sign out</Button>
    </div>
  </section>
);

export const RetryCard = ({ reason, onSignIn }: { reason: RetryReason; onSignIn: () => void }): ReactNode => (
  <section className="stack" aria-labelledby="retry-title">
    <h1 id="retry-title" className="t-display">
      {RETRY_COPY[reason].title}
    </h1>
    <p className="t-body measure">{RETRY_COPY[reason].body}</p>
    <div className="gate-actions">
      <Button variant="primary" onClick={onSignIn}>
        Sign in
      </Button>
    </div>
  </section>
);

export const LoadErrorCard = ({ onRetry }: { onRetry: () => void }): ReactNode => (
  <div className="stack">
    <ErrorText aria-label="Portal session load error">Could not load your portal session.</ErrorText>
    <div>
      <Button onClick={onRetry}>Try again</Button>
    </div>
  </div>
);

export const GateLoading = (): ReactNode => <Feedback>Loading…</Feedback>;
